import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useMultiSheetWorkflow } from '@/hooks/useMultiSheetWorkflow';
import { useLayerNavigation } from '@/hooks/useLayerNavigation';

type MultiSheetReturn = ReturnType<typeof useMultiSheetWorkflow>;
type LayerNavReturn = ReturnType<typeof useLayerNavigation>;

/**
 * Flat interface that fuses workflow state + mutations + layer-navigation.
 * Renames `currentGroupId` → `currentLayerGroupId` to match the historical
 * Index.tsx destructure call-sites (so consumers migrate without renaming).
 */
export interface WorkflowContextValue extends Omit<MultiSheetReturn, never>,
  Omit<LayerNavReturn, 'currentGroupId'> {
  currentLayerGroupId: LayerNavReturn['currentGroupId'];
}

const WorkflowContext = createContext<WorkflowContextValue | null>(null);

/**
 * Provider that owns the full workflow state-tree:
 *  - useMultiSheetWorkflow → blocks/connectors/groups/sheets + all mutations
 *  - useLayerNavigation    → derived layer-aware visibility + breadcrumbs
 *
 * Index.tsx wraps its render in this provider so descendants (sidebars,
 * canvas, dialogs, shells) can pull what they need via `useWorkflow()`
 * instead of receiving 30+ drilled props.
 */
export function WorkflowProvider({ children }: { children: ReactNode }) {
  const ms = useMultiSheetWorkflow();
  const ln = useLayerNavigation(ms.blocks, ms.connectors, ms.groups);

  // Memo on the underlying refs — when ms or ln return new objects (every
  // render today) consumers re-render. That's correct: any consumer using
  // workflow data should re-render when workflow data changes. Stable
  // identity here is therefore not the goal.
  const value = useMemo<WorkflowContextValue>(() => ({
    ...ms,
    layerPath: ln.layerPath,
    currentLayerGroupId: ln.currentGroupId,
    currentLayerGroups: ln.currentLayerGroups,
    collapsedGroups: ln.collapsedGroups,
    expandedGroups: ln.expandedGroups,
    visibleBlocks: ln.visibleBlocks,
    visibleConnectors: ln.visibleConnectors,
    breadcrumbs: ln.breadcrumbs,
    navigateInto: ln.navigateInto,
    navigateUp: ln.navigateUp,
    navigateToLevel: ln.navigateToLevel,
    resetLayerPath: ln.resetLayerPath,
  }), [ms, ln]);

  return <WorkflowContext.Provider value={value}>{children}</WorkflowContext.Provider>;
}

/**
 * Consume the workflow context. Throws if called outside <WorkflowProvider>
 * so missing-provider bugs surface at mount instead of as silent undefined
 * destructures.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useWorkflow(): WorkflowContextValue {
  const ctx = useContext(WorkflowContext);
  if (!ctx) {
    throw new Error('useWorkflow must be used within a <WorkflowProvider>');
  }
  return ctx;
}

/**
 * Variant av useWorkflow som returnerar `null` istället för att kasta
 * när providern saknas. Används av komponenter som måste fungera både
 * innanför app-shell:en (där providern finns) och i isolerade test-
 * mounts (där de inte rimligen kan dra in hela WorkflowProvider). Pull
 * bara fält du har en sane fallback för — annars använd useWorkflow.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useWorkflowOptional(): WorkflowContextValue | null {
  return useContext(WorkflowContext);
}
